import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  IconButton
} from '@mui/material';
import PropTypes from 'prop-types';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { DOCUMENTS_UPLOAD_URL, DOCUMENTS_INDEXING_STATUS_LATEST_URL } from 'api/documents';
import { useNotifications } from 'contexts/notification-context';
import useHttp from 'hooks/useHttp';
import { theme } from 'utils/theme';

const EmployeeManualUploadModal = ({ open, onClose }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [indexingStatus, setIndexingStatus] = useState(null);
  const fileInputRef = useRef(null);
  const { latestNotification } = useNotifications();

  const {
    data: lastIndexingStatus,
    error: errorGettingLastStatus,
    sendRequest: getLastIndexingStatus } = useHttp();
  const {
    error: errorUploadDocument,
    sendRequest: uploadEmployeeManual,
    loading: isUploadingFile,
    clearData: clearUploadData
  } = useHttp();

  useEffect(() => {
    if (open) {
      getLastIndexingStatus({ url: DOCUMENTS_INDEXING_STATUS_LATEST_URL, method: 'GET' });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setIsDragOver(false);
    }
  }, [open]);

  useEffect(() => {
    if (errorUploadDocument) {
      setIndexingStatus({ status: 'Failure', timestamp: new Date(), message: errorUploadDocument.message });
      return;
    }

    if (latestNotification) {
      setIndexingStatus({
        status: latestNotification.status,
        finishedAtUtc: latestNotification.timestamp,
        receivedAt: latestNotification.receivedAt
      });
      return;
    }

    if (lastIndexingStatus) {
      setIndexingStatus(lastIndexingStatus);
      return;
    }

    if (errorGettingLastStatus) {
      setIndexingStatus({ status: 'No file found', timestamp: new Date(), message: errorGettingLastStatus.message });
    }
  }, [latestNotification, lastIndexingStatus, errorUploadDocument, errorGettingLastStatus]);


  const selectFile = (file) => {
    if (file?.type === 'application/pdf') {
      setSelectedFile(file);
      clearUploadData();
    } else if (file) {
      setSelectedFile(null);
    }
  };

  const handleFileChange = (e) => {
    selectFile(e.target.files[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    selectFile(e.dataTransfer.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      return;
    }

    setIndexingStatus({ status: 'InProgress', timestamp: new Date() });

    const formData = new FormData();
    formData.append('file', selectedFile);

    await uploadEmployeeManual({ url: DOCUMENTS_UPLOAD_URL, method: 'POST', data: formData });
  };

  const formattedTime = indexingStatus?.finishedAtUtc ? new Date(indexingStatus.finishedAtUtc).toLocaleTimeString() : '';
  const formattedDate = indexingStatus?.finishedAtUtc ? new Date(indexingStatus.finishedAtUtc).toLocaleDateString() : '';

  const statusMap = useMemo(() => ({
    InProgress: { severity: 'info', title: 'Processing...', message: 'Your file has been uploaded and is being indexed. You can close the modal, you will be notified when the indexing is complete.' },
    Success: { severity: 'success', title: 'Success', message: `Indexed successfully at ${formattedTime} on ${formattedDate}.` },
    Failure: { severity: 'error', title: 'Failure', message: indexingStatus?.message || `Indexing failed at ${formattedTime} on ${formattedDate}.` },
    'No file found': { severity: 'info', title: 'Information', message: 'No Employee manual uploaded yet. Please upload a PDF.' }
  }), [indexingStatus, formattedTime, formattedDate]);

  const renderStatusAlert = () => {
    if (!indexingStatus) {
      return null;
    }
    const { severity, title, message } = statusMap[indexingStatus.status] || {};
    if (!severity) {
      return null;
    }

    return (
      <Alert severity={severity} sx={{ mt: 4 }}>
        <AlertTitle>{title}</AlertTitle>
        {message}
      </Alert>
    );
  };

  const isProcessing = isUploadingFile || indexingStatus?.status === 'InProgress';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ padding: 0, backgroundColor: theme.palette.grey[50] }}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ padding: '30px', backgroundColor: 'white', margin: '16px', borderRadius: 1 }}>
          <Typography sx={{ textAlign: 'center', marginBottom: 3 }}>
            Upload a new version of the employee manual PDF to update the chatbot knowledge.
          </Typography>
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? theme.palette.primary.main : theme.palette.grey[400],
                borderRadius: 2,
                padding: '40px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragOver ? theme.palette.action.hover : 'transparent',
                transition: 'background-color 0.3s, border-color 0.3s',
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="application/pdf" style={{ display: 'none' }} />
              <CloudUploadIcon sx={{ fontSize: 60, color: theme.palette.grey[500] }} />
              <Typography variant="h6">Drag & drop your PDF here</Typography>
              <Typography color={theme.palette.text.secondary}>or</Typography>
              <Button variant="outlined" sx={{ marginTop: 1 }}>Browse File</Button>
            </Box>
            {selectedFile && (
              <Box sx={{ marginTop: '24px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid', borderColor: theme.palette.grey[300], borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InsertDriveFileIcon sx={{ marginRight: '10px', color: theme.palette.text.secondary }} />
                  <Typography>{selectedFile.name}</Typography>
                </Box>
                <IconButton size="small" onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                  }
                }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
            <Box sx={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!selectedFile || isProcessing}
                startIcon={isUploadingFile ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isUploadingFile ? 'Processing...' : 'Upload and Index Manual'}
              </Button>
            </Box>
          </form>
          {renderStatusAlert()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

EmployeeManualUploadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EmployeeManualUploadModal;